<template>
    <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
            <div>
                <span class="text-xl">Data connections</span>
                <div class="text-sm text-surface-500 dark:text-surface-400">
                    The systems PAC reads event logs from and publishes results to. Workspaces choose one of them.
                </div>
            </div>
            <Button rounded icon="pi pi-plus" label="New connection" data-cy="newDataConnection" @click="openNew"/>
        </div>

        <DataTable :value="connections" :loading="loading" dataKey="id">
            <Column field="name" header="Name"></Column>
            <Column header="Type">
                <template #body="{data}">{{ connectorTitle(data.connector_type) }}</template>
            </Column>
            <Column header="" style="width: 8rem">
                <template #body="{data}">
                    <Button text rounded icon="pi pi-pencil" v-tooltip.top="'Edit'" @click="openEdit(data)"/>
                    <Button text rounded severity="danger" icon="pi pi-trash" v-tooltip.top="'Delete'"
                            @click="confirmDelete(data)"/>
                </template>
            </Column>
        </DataTable>
        <Message v-if="listError" severity="error">{{ listError }}</Message>

        <Dialog v-model:visible="dialogVisible" :header="editing ? `Edit ${editing.name}` : 'New data connection'"
                :style="{width: '640px'}" modal>
            <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                    <label for="connection-name" class="font-bold">Name *</label>
                    <InputText id="connection-name" v-model="form.name" :invalid="showErrors && !form.name"/>
                </div>
                <div class="flex flex-col gap-1">
                    <label for="connection-type" class="font-bold">Type *</label>
                    <Select inputId="connection-type" v-model="form.connectorType" :options="availableConnectors"
                            optionLabel="title" optionValue="type" :disabled="Boolean(editing)"
                            :invalid="showErrors && !form.connectorType" @update:modelValue="form.connection = {}"/>
                    <small v-if="connector" class="text-surface-500">{{ connector.description }}</small>
                </div>
                <SchemaForm v-if="connector" :schema="connector.connection_schema" v-model="form.connection"
                            :showErrors="showErrors" idPrefix="connection-"/>
                <div class="text-sm text-surface-500" v-if="connector">
                    Secrets are stored in the environment's secret store; only a reference is kept here.
                </div>
                <Message v-if="testResult" :severity="testResult.ok ? 'success' : 'error'">{{ testResult.message }}</Message>
                <Message v-if="saveError" severity="error">{{ saveError }}</Message>
            </div>
            <template #footer>
                <Button rounded text icon="pi pi-bolt" label="Test" class="mr-auto" :disabled="!connector || busy"
                        :loading="testing" @click="test"/>
                <Button rounded text icon="pi pi-times" label="Cancel" @click="dialogVisible = false"/>
                <Button rounded icon="pi pi-check" :label="editing ? 'Save' : 'Create'" :disabled="busy" :loading="saving"
                        @click="save"/>
            </template>
        </Dialog>
    </div>
</template>

<script setup>
// Managing data connections (manage_settings permission, enforced by the API).
import {computed, onMounted, ref} from 'vue';
import {useStore} from 'vuex';
import {useConfirm} from 'primevue/useconfirm';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Select from 'primevue/select';
import SchemaForm from '@/src/shared-components/SchemaForm.vue';
import {missingRequired, valuesForSubmit, withDefaults} from '@/src/utils/connectorSchema';
import {isApiError} from '@/src/api/http';

const store = useStore();
const confirm = useConfirm();

const loading = ref(false);
const listError = ref('');
const connections = computed(() => store.getters['connectionStore/connections']);
const availableConnectors = computed(() => store.getters['connectionStore/connectors'].filter(item => item.available));
const connectorTitle = (type) => store.getters['connectionStore/connector'](type)?.title || type;

const errorMessage = (error, fallback) => (isApiError(error) ? error.message : fallback);

const load = async () => {
    loading.value = true;
    try {
        await store.dispatch('connectionStore/loadAll', {force: true});
    } catch (error) {
        listError.value = errorMessage(error, 'Could not load the data connections.');
    } finally {
        loading.value = false;
    }
};
onMounted(load);

const dialogVisible = ref(false);
const editing = ref(null);
const form = ref({name: '', connectorType: null, connection: {}});
const showErrors = ref(false);
const testResult = ref(null);
const saveError = ref('');
const testing = ref(false);
const saving = ref(false);
const busy = computed(() => testing.value || saving.value);
const connector = computed(() => store.getters['connectionStore/connector'](form.value.connectorType));

const openDialog = (values, connection) => {
    editing.value = connection;
    form.value = values;
    showErrors.value = false;
    testResult.value = null;
    saveError.value = '';
    dialogVisible.value = true;
};

const openNew = () => openDialog({name: '', connectorType: null, connection: {}}, null);

const openEdit = async (connection) => {
    const detail = await store.dispatch('connectionStore/getConnection', connection.id);
    const schema = store.getters['connectionStore/connector'](detail.connector_type)?.connection_schema || {};
    openDialog({name: detail.name, connectorType: detail.connector_type,
                connection: withDefaults(schema, detail.connection || {})}, detail);
};

const submittedSettings = () => valuesForSubmit(connector.value.connection_schema, form.value.connection);

const test = async () => {
    testing.value = true;
    testResult.value = null;
    try {
        testResult.value = await store.dispatch('connectionStore/testConnection', {
            connectionId: editing.value?.id, connectorType: form.value.connectorType, connection: submittedSettings()});
    } catch (error) {
        testResult.value = {ok: false, message: errorMessage(error, 'The connection test failed.')};
    } finally {
        testing.value = false;
    }
};

const save = async () => {
    showErrors.value = true;
    saveError.value = '';
    if (!form.value.name || !connector.value ||
        missingRequired(connector.value.connection_schema, form.value.connection).length) {
        return;
    }
    saving.value = true;
    try {
        await store.dispatch('connectionStore/saveConnection', {
            connectionId: editing.value?.id, name: form.value.name, connectorType: form.value.connectorType,
            connection: submittedSettings()});
        dialogVisible.value = false;
    } catch (error) {
        saveError.value = errorMessage(error, 'The data connection could not be saved.');
    } finally {
        saving.value = false;
    }
};

const confirmDelete = (connection) => {
    confirm.require({
        message: `Delete the data connection "${connection.name}"?`,
        header: 'Delete data connection',
        acceptLabel: 'Delete',
        rejectLabel: 'Cancel',
        accept: async () => {
            listError.value = '';
            try {
                await store.dispatch('connectionStore/deleteConnection', connection.id);
            } catch (error) {
                listError.value = errorMessage(error, 'The data connection could not be deleted.');
            }
        },
    });
};
</script>
